// Global imports
import { clsx } from 'clsx'
import { useRef, useState } from 'react'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import {
  ContentEditor,
  Heading,
  Image,
  Notice,
  NumberedCardGrid,
} from '~/components'
import { useScrollOverflow } from '~/hooks'

// Types
import type { PortableTextBlock } from '@portabletext/react'
import type {
  NumberedCardItem,
  NumberedCardMobileView,
  SanityRefWithAlt,
} from '~/types'

interface StepTab {
  icon?: SanityRefWithAlt
  title: string
  heading?: string
  description?: string
  cards?: NumberedCardItem[]
  notice?: string
}

interface BlockStepTabsProps {
  content?: PortableTextBlock[]
  tabsMobileLayout?: 'stacked' | 'carousel'
  mobileLayout?: NumberedCardMobileView
  tabs?: StepTab[]
}

// Round arrow buttons, in the same style as the carousel arrows
const arrowClasses =
  'absolute top-1/2 z-10 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white drop-shadow-md'

// Main export
export default function BlockStepTabs({
  content,
  tabsMobileLayout = 'carousel',
  mobileLayout = 'stacked',
  tabs,
}: BlockStepTabsProps) {
  const tabListRef = useRef<HTMLDivElement>(null)
  const [selected, setSelected] = useState(0)

  const { canScrollPrev, canScrollNext, scrollStep } =
    useScrollOverflow(tabListRef)

  if (!tabs?.length) return null

  // Stacked tabs sit one above the other on phones, so they never overflow
  // and no arrows show. From tablet width up the tabs are always a row
  const tabsStacked = tabsMobileLayout === 'stacked'

  // Panels stay mounted and are hidden instead, so every tab's content is in
  // the server rendered HTML rather than only the first. The tab row bleeds
  // into the container gutter so it can scroll sideways on small screens while
  // the first tab still lines up with the grid. When tabs are cut off, that
  // edge fades out and an arrow appears, so it's clear there are more.
  // A carousel can't measure itself while its panel is hidden, so the cards
  // are redrawn when their tab is opened
  return (
    <div className="container">
      {content && (
        <div className="mb-6 max-w-2xl">
          <ContentEditor content={content} />
        </div>
      )}
      <TabGroup
        selectedIndex={selected}
        onChange={setSelected}
      >
        <div className="relative -mx-6 mb-6">
          <TabList
            ref={tabListRef}
            className={clsx(
              'flex px-6 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
              {
                'gap-4 overflow-x-auto': !tabsStacked,
                'flex-col gap-3 md:flex-row md:gap-4 md:overflow-x-auto':
                  tabsStacked,
                '[mask-image:linear-gradient(to_right,transparent,black_4rem,black_calc(100%_-_4rem),transparent)]':
                  canScrollPrev && canScrollNext,
                '[mask-image:linear-gradient(to_right,transparent,black_4rem)]':
                  canScrollPrev && !canScrollNext,
                '[mask-image:linear-gradient(to_right,black_calc(100%_-_4rem),transparent)]':
                  !canScrollPrev && canScrollNext,
              }
            )}
          >
            {tabs.map((tab, i) => (
              <Tab
                key={'tab-' + i}
                className={clsx(
                  'flex flex-none items-center gap-2 rounded-full border border-teal-green py-2.5 pr-6 font-medium text-neutral-900 data-[selected]:bg-teal-green data-[selected]:font-semibold data-[selected]:text-white',
                  {
                    'pl-2': tab.icon?.asset,
                    'pl-6': !tab.icon?.asset,
                  }
                )}
              >
                {tab.icon?.asset && (
                  <span className="flex size-10 flex-none items-center justify-center rounded-full bg-white">
                    <Image
                      image={tab.icon}
                      customSize={48}
                      className="max-h-6 max-w-6 object-contain"
                    />
                  </span>
                )}
                {tab.title}
              </Tab>
            ))}
          </TabList>
          {canScrollPrev && (
            <button
              type="button"
              aria-label="Show earlier tabs"
              className={clsx(arrowClasses, 'left-2')}
              onClick={() => scrollStep(-1)}
            >
              <ChevronLeftIcon className="size-5 text-teal-green" />
            </button>
          )}
          {canScrollNext && (
            <button
              type="button"
              aria-label="Show more tabs"
              className={clsx(arrowClasses, 'right-2')}
              onClick={() => scrollStep(1)}
            >
              <ChevronRightIcon className="size-5 text-teal-green" />
            </button>
          )}
        </div>
        <TabPanels>
          {tabs.map((tab, i) => (
            <TabPanel
              key={'tabpanel-' + i}
              unmount={false}
              className="flex flex-col gap-7 rounded-2xl bg-white p-6"
            >
              {(tab.heading || tab.description) && (
                <div>
                  {tab.heading && (
                    <Heading
                      className="mb-2"
                      tag="h3"
                      size="h5"
                    >
                      {tab.heading}
                    </Heading>
                  )}
                  {tab.description && <p className="mb-0">{tab.description}</p>}
                </div>
              )}
              {tab.cards && tab.cards.length > 0 && (
                <NumberedCardGrid
                  key={selected === i ? 'shown' : 'hidden'}
                  cards={tab.cards}
                  mobileView={mobileLayout}
                  outlined
                />
              )}
              {tab.notice && <Notice variant="error">{tab.notice}</Notice>}
            </TabPanel>
          ))}
        </TabPanels>
      </TabGroup>
    </div>
  )
}
